import { ChecklistBlockConfigurationRecord } from "models/view-models/block-configurations/checklist-block-configuration-record";
import React, { useCallback, useMemo } from "react";
import { PageBlockContentProps } from "./page-block-content";
import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import { ChecklistStatus } from "models/enumerations/notebooks/checklist-item-status";
import useChecklistItem from "utilities/hooks/domain/notebooks/pages/checklists/use-checklist-item";
import { BlockRecord } from "models/view-models/block-record";
import ChecklistItemDisplay from "organisms/checklist-block-editor/checklist-item-display";
import { RecordUtils } from "@rsm-hcd/javascript-core";
import {
    AdditionalChecklistBlockData,
    DEFAULT_ADDITIONAL_CHECKLIST_BLOCK_DATA,
    isChecklistAdditionalData,
} from "models/interfaces/additional-block-data/additional-checklist-block-data";
import { ChecklistItemNoteRecord } from "models/view-models/checklist-item-notes/checklist-item-note-record";

interface ChecklistBlockContentProps extends PageBlockContentProps {}

type ItemUpdater =
    | ChecklistItemRecord[]
    | ((prevItems: ChecklistItemRecord[]) => ChecklistItemRecord[]);

const CSS_CLASS_NAME = "c-checklist-block-content";

const ChecklistBlockContent = ({
    block,
    onBlockSaved,
    printable,
    additionalBlockData,
}: ChecklistBlockContentProps) => {
    const { updateChecklistItem } = useChecklistItem();

    const checklistBlockConfiguration = useMemo(() => {
        return RecordUtils.ensureRecord(
            block.configuration,
            ChecklistBlockConfigurationRecord
        );
    }, [block.configuration]);

    const {
        guardAction,
        items,
        updateItems: updateItemsFromAdditionalBlockData,
    } = useMemo((): AdditionalChecklistBlockData => {
        if (!isChecklistAdditionalData(additionalBlockData)) {
            return DEFAULT_ADDITIONAL_CHECKLIST_BLOCK_DATA;
        }

        return additionalBlockData;
    }, [additionalBlockData]);

    const updateChecklistItems = useCallback(
        (updateItems: ItemUpdater): void => {
            const updatedItems =
                typeof updateItems === "function"
                    ? updateItems(items)
                    : updateItems;

            updateItemsFromAdditionalBlockData(updatedItems);
        },
        [items, updateItemsFromAdditionalBlockData]
    );

    const updateItemInChecklist = (updatedItem: ChecklistItemRecord): void => {
        updateChecklistItems((prev) =>
            prev.map((item) =>
                item.id === updatedItem.id
                    ? updatedItem.with({ note: updatedItem.note ?? item.note })
                    : item
            )
        );
    };

    const handleSaveChecklistItemNote = (
        itemWithUpdatedNote: ChecklistItemRecord,
        updatedNote?: ChecklistItemNoteRecord
    ) => {
        const updatedItem = itemWithUpdatedNote.with({ note: updatedNote });

        updateChecklistItems((prev) =>
            prev.map((item) =>
                item.id === updatedItem.id ? updatedItem : item
            )
        );
    };

    const handleUpdateChecklistItem = async (
        item: ChecklistItemRecord
    ): Promise<void> => {
        if (guardAction != null) {
            const canProceed = await guardAction();
            if (!canProceed) {
                return;
            }
        }

        try {
            const updatedItem = await updateChecklistItem(item);

            if (updatedItem == null) {
                throw new Error("Error updating checklist item");
            }

            updateItemInChecklist(updatedItem);
        } catch (error) {
            console.error("Error updating checklist item", error);

            onBlockSaved(block);

            return;
        }
    };

    const handleUpdateChecklistItemStatus = async (
        item: ChecklistItemRecord,
        newStatus: ChecklistStatus
    ): Promise<void> => {
        handleUpdateChecklistItem(item.with({ status: newStatus }));
    };

    const handleUpdateChecklistItemChecked = async (
        item: ChecklistItemRecord
    ): Promise<void> => {
        handleUpdateChecklistItem(item.with({ isChecked: !item.isChecked }));
    };

    return (
        <>
            <div className={CSS_CLASS_NAME}>
                {items.map((item, index) => (
                    <ChecklistItemDisplay
                        index={index}
                        isEditing={false}
                        isNumbered={checklistBlockConfiguration.isNumbered}
                        item={item}
                        key={item.id}
                        onChecklistItemNoteUpdate={(
                            updatedNote?: ChecklistItemNoteRecord
                        ) => handleSaveChecklistItemNote(item, updatedNote)}
                        onUpdateChecklistItemChecked={() =>
                            handleUpdateChecklistItemChecked(item)
                        }
                        onUpdateChecklistItemStatus={(newStatus) =>
                            handleUpdateChecklistItemStatus(item, newStatus)
                        }
                        printable={printable ?? false}
                        showStatus={
                            checklistBlockConfiguration.usesCustomStatuses
                        }
                        totalItems={items.length}
                    />
                ))}
            </div>
        </>
    );
};

export default ChecklistBlockContent;
